import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LocalizationProvider, MobileDatePicker, TimePicker } from '@mui/lab';
import {
  Autocomplete,
  Button,
  FormControl,
  Grid,
  InputAdornment,
  Stack,
  TextFieldProps,
  Typography,
} from '@mui/material';
import {
  Save as SaveIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import AdapterMoment from '@mui/lab/AdapterMoment';
import moment, { Moment } from 'moment';
import momentTimezone from 'moment-timezone';

import {
  TUpdateCourseAsAdmin,
  getCoursesState,
  updateCourseAsAdmin,
} from '../../../../../../../store/V2/CoursesSlice';
import {
  getCurriculumV2State,
  getCurriculumsV2,
} from '../../../../../../../store/V2/CurriculumSlice';
import { resetAdminCourseTutorsData } from '../../../../../../../store/V2/CourseTutorsSlice';
import { dayListOptions } from '../../../../../../../constants';
import { useAppSelector } from '../../../../../../../hooks';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../../components/Inputs';
import { TabPanel } from '..';
import { checkUserPermission } from '../../../../../../../helper';
import { getAuthState } from '../../../../../../../store/AuthSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../../store/UserSlice';

interface BasicDetailsTabProps {
  selectedTabValue: number;
  selectedTabIndex: number;
}

type TSelectOption = {
  label: string;
  value: string;
};

type TState = {
  selectedCurriculum: TSelectOption | null;
  selectedGrade: TSelectOption | null;
  selectedSubject: TSelectOption | null;
  numberOfClasses: string;
  classDuration: string;
  classDay: TSelectOption | null;
  classTime: Moment | null;
  courseStart: Moment | null;
  courseEnd: Moment | null;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const initialState: TState = {
  selectedCurriculum: null,
  selectedGrade: null,
  selectedSubject: null,
  numberOfClasses: '',
  classDuration: '',
  classDay: null,
  classTime: null,
  courseStart: null,
  courseEnd: null,
};

const BasicDetailsTab: React.FC<BasicDetailsTabProps> = ({
  selectedTabValue,
  selectedTabIndex,
}) => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState(initialState);
  const [gradeOptions, setGradeOptions] = useState<Array<TSelectOption>>([]);
  const [subjectOptions, setSubjectOptions] = useState<Array<TSelectOption>>(
    []
  );

  const [curriculumOptions, setCurriculumOptions] = useState<
    Array<TSelectOption>
  >([]);

  // Store
  const curriculumState = useAppSelector(getCurriculumV2State);
  const coursesState = useAppSelector(getCoursesState);
  const authState = useAppSelector(getAuthState);

  const courseData = coursesState.adminCourse;

  useEffect(() => {
    dispatch(getCurriculumsV2());

    if (courseData) {
      setState((prevState) => {
        const newState: TState = { ...prevState };

        if (courseData.curriculum) {
          newState.selectedCurriculum = {
            label: courseData.curriculum,
            value: courseData.curriculum,
          };
        }

        if (courseData.grade) {
          newState.selectedGrade = {
            label: courseData.grade,
            value: courseData.grade,
          };
        }

        if (courseData.subject) {
          newState.selectedSubject = {
            label: courseData.subject,
            value: courseData.subject,
          };
        }

        if (courseData.noOfClasses) {
          newState.numberOfClasses = courseData.noOfClasses.toString();
        }

        if (courseData.duration) {
          newState.classDuration = courseData.duration.toString();
        }

        if (courseData.classDay) {
          newState.classDay = {
            label: courseData.classDay,
            value: courseData.classDay,
          };
        }

        if (courseData.classTime) {
          const _utcMoment = momentTimezone.tz(
            courseData.classTime,
            'HH:mm',
            'UTC'
          );

          newState.classTime = _utcMoment.clone().tz(moment.tz.guess());
        }

        if (courseData.startDate) {
          newState.courseStart = moment(courseData.startDate);
        }

        if (courseData.endDate) {
          newState.courseEnd = moment(courseData.endDate);
        }

        return newState;
      });
    }

    return () => {
      dispatch(resetAdminCourseTutorsData());
    };
  }, []);

  useEffect(() => {
    if (curriculumState.list && curriculumState.list.length) {
      setCurriculumOptions(
        curriculumState.list.map((curriculumItem) => ({
          label: curriculumItem.curriculum,
          value: curriculumItem.curriculum,
        }))
      );

      const matchingCurriculum = curriculumState.list.find(
        (curriculumItem) =>
          curriculumItem.curriculum === state.selectedCurriculum?.value
      );

      if (
        matchingCurriculum &&
        matchingCurriculum.subjects &&
        matchingCurriculum.subjects.length
      ) {
        const newSubjectOptions: TSelectOption[] = [];

        matchingCurriculum.subjects.forEach((subjectItem) => {
          newSubjectOptions.push({
            label: subjectItem.subject,
            value: subjectItem.subject,
          });
        });

        setSubjectOptions(newSubjectOptions);

        const matchingSubject = matchingCurriculum.subjects.find(
          (subjectItem) => subjectItem.subject === state.selectedSubject?.value
        );

        if (
          matchingSubject &&
          matchingSubject.grade &&
          matchingSubject.grade.length
        ) {
          const newGradeOptions: TSelectOption[] = [];

          matchingSubject.grade.forEach((gradeItem) => {
            newGradeOptions.push({
              label: gradeItem.grade,
              value: gradeItem.grade,
            });
          });

          setGradeOptions(newGradeOptions);
        }
      }
    }
  }, [curriculumState.list]);

  const handleCurriculumChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    const newSubjectOptions: TSelectOption[] = [];

    if (curriculumState.list && curriculumState.list.length && value) {
      const selectedCurriculumData = curriculumState.list.find(
        (item) => item.curriculum === value.value
      );

      if (
        selectedCurriculumData &&
        selectedCurriculumData.subjects &&
        selectedCurriculumData.subjects.length
      ) {
        selectedCurriculumData.subjects.forEach((item) => {
          newSubjectOptions.push({
            label: item.subject,
            value: item.subject,
          });
        });
      }
    }

    setState((prevState) => ({
      ...prevState,
      selectedCurriculum: value,
      selectedSubject: null,
      selectedGrade: null,
    }));

    setSubjectOptions(newSubjectOptions);
    setGradeOptions([]);
  };

  const handleSubjectChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    const newGradeOptions: TSelectOption[] = [];

    if (state.selectedCurriculum) {
      const selectedCurriculumData = curriculumState.list.find(
        (curriculumItem) =>
          curriculumItem.curriculum === state.selectedCurriculum?.value
      );

      if (
        selectedCurriculumData &&
        selectedCurriculumData.subjects &&
        selectedCurriculumData.subjects.length
      ) {
        const selectedSubject = selectedCurriculumData.subjects.find(
          (subjectItem) => subjectItem.subject === value?.value
        );

        if (
          selectedSubject &&
          selectedSubject.grade &&
          selectedSubject.grade.length
        ) {
          selectedSubject.grade.forEach((gradeItem) => {
            newGradeOptions.push({
              label: gradeItem.grade,
              value: gradeItem.grade,
            });
          });
        }
      }
    }

    setState((prevState) => ({
      ...prevState,
      selectedSubject: value,
      selectedGrade: null,
    }));

    setGradeOptions(newGradeOptions);
  };

  const handleGradeChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedGrade: value,
    }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;

    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleClassDayChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      classDay: value,
    }));
  };

  const handleClassTimeChange = (value: Moment | null) => {
    setState((prevState) => ({
      ...prevState,
      classTime: value,
    }));
  };

  const handleCourseStartDateChange = (value: Moment | null) => {
    if (value) {
      setState((prevState) => ({
        ...prevState,
        courseStart: value,
      }));
    }
  };

  const handleCourseEndDateChange = (value: Moment | null) => {
    if (value) {
      setState((prevState) => ({
        ...prevState,
        courseEnd: value,
      }));
    }
  };

  const handleSubmit = () => {
    if (courseData) {
      const apiData: TUpdateCourseAsAdmin = {
        _id: courseData._id,
      };

      if (state.selectedCurriculum) {
        apiData.curriculum = state.selectedCurriculum.value;
      }

      if (state.selectedGrade) {
        apiData.grade = state.selectedGrade.value;
      }

      if (state.selectedSubject) {
        apiData.subject = state.selectedSubject.value;
      }

      if (state.numberOfClasses) {
        apiData.noOfClasses = Number(state.numberOfClasses);
      }

      if (state.classDuration) {
        apiData.duration = Number(state.classDuration);
      }

      if (state.classDay) {
        apiData.classDay = state.classDay.value;
      }

      if (state.classTime) {
        apiData.classTime = state.classTime.clone().utc().format('HH:mm');
      }

      if (state.courseStart) {
        apiData.startDate = state.courseStart.clone().toISOString();
      }

      if (state.courseEnd) {
        apiData.endDate = state.courseEnd.clone().toISOString();
      }

      if (Object.keys(apiData).length > 1) {
        dispatch(updateCourseAsAdmin(apiData));
      }
    }
  };

  return (
    <TabPanel value={selectedTabValue} index={selectedTabIndex}>
      <div style={{ marginBottom: '30px' }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">Basic Details</Typography>
          {checkUserPermission(authState.user, {
            adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT],
          }) ? (
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
            >
              Save
            </Button>
          ) : null}
        </Stack>
      </div>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <FormControl fullWidth>
            <Autocomplete
              size="small"
              value={state.selectedCurriculum}
              options={curriculumOptions}
              onChange={handleCurriculumChange}
              popupIcon={TPAutoCompleteArrowIconComponent}
              loading={curriculumState.status === 'loading'}
              isOptionEqualToValue={(option, value) =>
                option.label === value.value
              }
              renderInput={(params) => (
                <TPTextField
                  {...params}
                  {...textFieldProps}
                  size="small"
                  label="Curriculum"
                  placeholder="Curriculum"
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <FormControl fullWidth>
            <Autocomplete
              size="small"
              value={state.selectedSubject}
              options={subjectOptions}
              onChange={handleSubjectChange}
              disabled={!state.selectedCurriculum}
              popupIcon={TPAutoCompleteArrowIconComponent}
              loading={curriculumState.status === 'loading'}
              isOptionEqualToValue={(option, value) =>
                option.label === value.value
              }
              renderInput={(params) => (
                <TPTextField
                  {...params}
                  {...textFieldProps}
                  size="small"
                  label="Subject"
                  placeholder="Subject"
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <FormControl fullWidth>
            <Autocomplete
              size="small"
              value={state.selectedGrade}
              options={gradeOptions}
              onChange={handleGradeChange}
              disabled={!state.selectedSubject}
              popupIcon={TPAutoCompleteArrowIconComponent}
              loading={curriculumState.status === 'loading'}
              isOptionEqualToValue={(option, value) =>
                option.label === value.value
              }
              renderInput={(params) => (
                <TPTextField
                  {...params}
                  {...textFieldProps}
                  size="small"
                  label="Grade"
                  placeholder="Grade"
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <FormControl fullWidth>
            <TPTextField
              {...textFieldProps}
              name="numberOfClasses"
              type="number"
              size="small"
              label="No. of Classes"
              placeholder="No. of Classes"
              value={state.numberOfClasses}
              onChange={handleInputChange}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <FormControl fullWidth>
            <TPTextField
              {...textFieldProps}
              name="classDuration"
              type="number"
              size="small"
              label="Class Duration (mins)"
              placeholder="Class Duration (mins)"
              value={state.classDuration}
              onChange={handleInputChange}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <FormControl fullWidth>
            <Autocomplete
              size="small"
              value={state.classDay}
              options={dayListOptions}
              onChange={handleClassDayChange}
              popupIcon={TPAutoCompleteArrowIconComponent}
              renderInput={(params) => (
                <TPTextField
                  {...params}
                  {...textFieldProps}
                  size="small"
                  label="Class Day"
                  placeholder="Class Day"
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <FormControl fullWidth>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <TimePicker
                value={state.classTime}
                onChange={handleClassTimeChange}
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    label="Class Time"
                    placeholder="Class Time"
                    helperText={
                      state.classTime ? `(${deviceTimezone})` : undefined
                    }
                  />
                )}
              />
            </LocalizationProvider>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <FormControl fullWidth>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <MobileDatePicker
                disableCloseOnSelect={false}
                value={state.courseStart}
                onChange={handleCourseStartDateChange}
                label="Start Date"
                inputFormat="YYYY MMM DD"
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    placeholder="Start Date"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <InputAdornment position="end">
                          <DateRangeIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <FormControl fullWidth>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <MobileDatePicker
                disableCloseOnSelect={false}
                value={state.courseEnd}
                onChange={handleCourseEndDateChange}
                label="End Date"
                inputFormat="YYYY MMM DD"
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    placeholder="End Date"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <InputAdornment position="end">
                          <DateRangeIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </FormControl>
        </Grid>
      </Grid>
    </TabPanel>
  );
};

export default BasicDetailsTab;
